// src/screens/s2/SalesMarginAnalysis.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Session 2 Screen 5 — Sales and Margin Analysis.
// 2×2 grid: client concentration donut (top 5 + Other), gross margin trend
// across FYs with 2-year average reference line, avg-fee comparison between
// new and retained clients, and revenue by tracking category when enabled.
// Auto-flags concentration / margin compression / pricing stagnation / churn.
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import {
  PieChart, Pie, Cell, LineChart, Line, BarChart, Bar, XAxis, YAxis,
  Tooltip, CartesianGrid, ResponsiveContainer, ReferenceLine, Legend,
} from 'recharts';
import { ClientData } from '../../types/ClientData';
import { useXeroSales } from '../../hooks/useXeroSales';
import { formatCurrency, formatPercent } from '../../utils/formatters';

interface Props {
  data: ClientData;
  onUpdate: (updates: Partial<ClientData>) => void;
}

interface Flag { tone: 'warn' | 'info' | 'good'; message: string; }

const SLICE_COLOURS = ['#1E3A5F', '#60A5FA', '#10B981', '#C9A84C', '#F59E0B', '#94A3B8'];

function ChartCard({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5">
      <h3 className="text-sm font-bold text-ma-navy uppercase tracking-wide mb-1">{title}</h3>
      {subtitle && <p className="text-xs text-gray-500 mb-3">{subtitle}</p>}
      <div style={{ width: '100%', height: 260 }}>{children}</div>
    </div>
  );
}

export default function SalesMarginAnalysis({ data }: Props) {
  const sales = useXeroSales(data.xeroTenantId);

  if (sales.loading) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <div className="text-sm text-gray-500">Loading sales analysis…</div>
      </div>
    );
  }
  if (!sales.data) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <h2 className="text-2xl font-bold text-ma-navy mb-4">Sales &amp; Margin Analysis</h2>
        <div className="rounded-2xl bg-amber-50 border-2 border-amber-300 p-5 text-sm text-amber-800">
          {sales.error || 'No sales data available.'}
        </div>
      </div>
    );
  }

  const d = sales.data;

  // ── Client concentration (top 5 + Other) ────────────────────────────────
  // Sort by lifetime revenue from Xero then collapse the long tail into a
  // single "Other" slice so the donut stays readable.
  const sortedClients = [...d.clientDetail].sort((a, b) => b.totalRevenue - a.totalRevenue);
  const top5 = sortedClients.slice(0, 5);
  const rest = sortedClients.slice(5);
  const restTotal = rest.reduce((s, c) => s + c.totalRevenue, 0);
  const concentrationData = [
    ...top5.map((c) => ({ name: c.name, value: c.totalRevenue })),
    ...(restTotal > 0 ? [{ name: `Other (${rest.length})`, value: restTotal }] : []),
  ];
  const concentrationTotal = concentrationData.reduce((s, c) => s + c.value, 0);

  // ── Margin trend (FY grain) ────────────────────────────────────────────
  const marginSeries = d.yearlyKPIs.map((y) => ({ year: y.year, grossMargin: y.grossMargin }));
  const completeYears = d.yearlyKPIs.filter((y) => !y.year.endsWith('YTD'));
  const marginAvg = completeYears.length > 0
    ? Math.round(completeYears.reduce((s, y) => s + y.grossMargin, 0) / completeYears.length)
    : 0;

  // ── New vs retained avg fee — split clientDetail by firstInvoice age ───
  // Anything whose firstInvoice landed in the last 12 months counts as a new
  // client cohort; older clients are the retained cohort.
  const now = new Date();
  const cutoffRecent = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 12, now.getUTCDate()));
  const isNewCohort = (d2: string | null) => {
    if (!d2) return false;
    const t = new Date(d2);
    return !isNaN(t.getTime()) && t >= cutoffRecent;
  };
  const newClients = d.clientDetail.filter((c) => isNewCohort(c.firstInvoice));
  const retainedClients = d.clientDetail.filter((c) => c.lastInvoice && new Date(c.lastInvoice) >= cutoffRecent && !isNewCohort(c.firstInvoice));
  const avgFeeNew = newClients.length > 0
    ? Math.round(newClients.reduce((s, c) => s + c.avgFee, 0) / newClients.length)
    : 0;
  const avgFeeRetained = retainedClients.length > 0
    ? Math.round(retainedClients.reduce((s, c) => s + c.avgFee, 0) / retainedClients.length)
    : 0;
  const feeComparisonData = [
    { label: 'New', avgFee: avgFeeNew, count: newClients.length },
    { label: 'Retained', avgFee: avgFeeRetained, count: retainedClients.length },
  ];

  // ── Auto-detected flags ────────────────────────────────────────────────
  const flags: Flag[] = [];
  // Concentration risk
  const topClient = sortedClients[0];
  if (topClient && concentrationTotal > 0) {
    const pct = (topClient.totalRevenue / concentrationTotal) * 100;
    if (pct > 20) {
      flags.push({ tone: 'warn', message: `Concentration risk — ${topClient.name} is ${formatPercent(pct)} of total revenue.` });
    }
  }
  // Margin compression — compare last two complete FYs
  if (completeYears.length >= 2) {
    const latest = completeYears[completeYears.length - 1];
    const prior = completeYears[completeYears.length - 2];
    if (latest.grossMargin < prior.grossMargin) {
      flags.push({ tone: 'warn', message: `Margin compression — gross margin fell from ${prior.grossMargin}% to ${latest.grossMargin}% across the last two FYs.` });
    }
  }
  // Pricing stagnation — avgClientFee flat across completed FYs
  if (completeYears.length >= 2) {
    const latest = completeYears[completeYears.length - 1];
    const prior = completeYears[completeYears.length - 2];
    const feeDelta = Math.abs(latest.avgClientFee - prior.avgClientFee);
    const feeMean = (latest.avgClientFee + prior.avgClientFee) / 2;
    if (feeMean > 0 && (feeDelta / feeMean) < 0.03) {
      flags.push({ tone: 'info', message: `Pricing stagnation — average client fee essentially flat (${formatCurrency(prior.avgClientFee)} → ${formatCurrency(latest.avgClientFee)}).` });
    }
  }
  // Retention trending down
  if (completeYears.length >= 2) {
    const latest = completeYears[completeYears.length - 1];
    const prior = completeYears[completeYears.length - 2];
    if (latest.retentionRate != null && prior.retentionRate != null && latest.retentionRate < prior.retentionRate) {
      flags.push({ tone: 'warn', message: `Retention declining — ${prior.retentionRate}% to ${latest.retentionRate}%.` });
    }
  }

  const flagColour = (tone: Flag['tone']) =>
    tone === 'warn' ? 'bg-amber-50 border-amber-200 text-amber-800'
      : tone === 'good' ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
      : 'bg-blue-50 border-blue-200 text-blue-800';

  // ── Tracking category segments ─────────────────────────────────────────
  const tracking = d.trackingCategories;
  const trackingData = tracking && tracking[0]
    ? tracking[0].options.map((o) => ({ name: o.name, value: o.revenue }))
    : null;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* HEADER */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-ma-navy">
          Sales &amp; Margin Analysis
          {sales.tenantName && <span className="ml-2 text-sm font-normal text-gray-500">— {sales.tenantName}</span>}
        </h2>
        <p className="text-gray-500 mt-1 text-sm">
          Where revenue concentrates, where margin is drifting, and whether
          pricing is keeping pace.
        </p>
      </div>

      {/* FLAGS PANEL */}
      {flags.length > 0 && (
        <div className="mb-6 space-y-2">
          {flags.map((f, i) => (
            <div key={i} className={`rounded-xl border p-3 text-sm ${flagColour(f.tone)}`}>
              {f.message}
            </div>
          ))}
        </div>
      )}

      {/* 2×2 GRID OF CHARTS */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        {/* Chart 1 — concentration donut */}
        <ChartCard
          title="Client concentration"
          subtitle={`Top 5 clients + remainder. Total: ${formatCurrency(concentrationTotal)}`}
        >
          <ResponsiveContainer>
            <PieChart>
              <Pie
                data={concentrationData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={90}
                paddingAngle={2}
                isAnimationActive={false}
              >
                {concentrationData.map((_, i) => (
                  <Cell key={i} fill={SLICE_COLOURS[i % SLICE_COLOURS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(v: any) => formatCurrency(Number(v))} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Chart 2 — margin trend */}
        <ChartCard
          title="Gross margin trend"
          subtitle={`Per FY. Dashed line shows ${marginAvg}% two-year average.`}
        >
          <ResponsiveContainer>
            <LineChart data={marginSeries} margin={{ top: 10, right: 8, bottom: 8, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="year" tick={{ fontSize: 11 }} stroke="#9CA3AF" />
              <YAxis tick={{ fontSize: 10 }} stroke="#9CA3AF" domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
              <Tooltip formatter={(v: any) => `${v}%`} />
              <ReferenceLine y={marginAvg} stroke="#94A3B8" strokeDasharray="4 2" label={{ value: `${marginAvg}% avg`, fontSize: 10, fill: '#94A3B8', position: 'insideTopRight' }} />
              <Line
                type="monotone"
                dataKey="grossMargin"
                stroke="#10B981"
                strokeWidth={3}
                dot={{ r: 5, fill: '#10B981' }}
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Chart 3 — fee comparison */}
        <ChartCard
          title="Fee comparison"
          subtitle="Avg fee: clients whose first invoice was within 12 months vs established."
        >
          <ResponsiveContainer>
            <BarChart data={feeComparisonData} margin={{ top: 10, right: 8, bottom: 8, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="#9CA3AF" />
              <YAxis tick={{ fontSize: 10 }} stroke="#9CA3AF" tickFormatter={(v) => `£${Math.round(v / 1000)}k`} />
              <Tooltip formatter={(v: any) => formatCurrency(Number(v))} />
              <Bar dataKey="avgFee" fill="#1E3A5F" radius={[4, 4, 0, 0]}>
                {feeComparisonData.map((_, i) => (
                  <Cell key={i} fill={i === 0 ? '#10B981' : '#1E3A5F'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Chart 4 — tracking categories donut (when present) */}
        {trackingData ? (
          <ChartCard
            title={`Revenue by ${tracking![0].name.toLowerCase()}`}
            subtitle="From Xero tracking categories."
          >
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={trackingData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={90}
                  paddingAngle={2}
                  isAnimationActive={false}
                >
                  {trackingData.map((_, i) => (
                    <Cell key={i} fill={SLICE_COLOURS[i % SLICE_COLOURS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v: any) => formatCurrency(Number(v))} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
        ) : (
          <div className="bg-gray-50 rounded-2xl border border-gray-200 p-5 flex flex-col items-center justify-center text-center">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">
              Revenue by category
            </div>
            <p className="text-sm text-gray-500 max-w-xs">
              Not available — this tenant isn't using Xero tracking categories.
              Enable tracking in Xero to see revenue segmented by service line.
            </p>
          </div>
        )}
      </div>

      {/* HEADLINE METRICS */}
      <div className="grid grid-cols-3 gap-4">
        <div className="rounded-2xl border border-gray-200 bg-white p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
            Top client share
          </div>
          <div className="text-2xl font-bold text-ma-navy">
            {topClient && concentrationTotal > 0
              ? formatPercent((topClient.totalRevenue / concentrationTotal) * 100)
              : '—'}
          </div>
          <div className="text-xs text-gray-500 mt-1">{topClient?.name || 'No client data'}</div>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
            Avg fee — new vs retained
          </div>
          <div className="text-2xl font-bold text-ma-navy">
            {formatCurrency(avgFeeNew)} <span className="text-gray-400 text-sm">vs</span> {formatCurrency(avgFeeRetained)}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {newClients.length} new · {retainedClients.length} retained
          </div>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
            Gross margin (latest FY)
          </div>
          <div className="text-2xl font-bold text-ma-navy">
            {completeYears.length > 0 ? `${completeYears[completeYears.length - 1].grossMargin}%` : '—'}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            2-year avg {marginAvg}%
          </div>
        </div>
      </div>
    </div>
  );
}
